import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiClient } from '@/utils/api'
import { Building2, GraduationCap, Loader2, Plus, Search, Trash2, X } from 'lucide-react'

/**
 * Scope #3 of the 2026-04-22 parent School pane review.
 *
 * Shows a parent's active district + school subscriptions grouped by district,
 * with self-service subscribe / unsubscribe flows. Uses the same HP Phoenix
 * endpoints the mobile app has been using since the school-onboarding release:
 *
 *   GET    /api/districts/subscriptions
 *   GET    /api/districts/nearby?state=XX&search=...
 *   POST   /api/districts/subscribe           { district_id, child_id?, child_name? }
 *   DELETE /api/districts/subscriptions/:id
 *   GET    /api/schools/subscriptions
 *   GET    /api/schools/search?q=...&state=XX
 *   POST   /api/schools/subscribe             { school_id, child_id?, child_name? }
 *   DELETE /api/schools/subscriptions/:id
 *
 * No backend work needed — the aggregation is done client-side by joining the
 * district and school subscription lists on district_id. Multi-kid assignment
 * issues N parallel subscribe calls (mobile does it the same way).
 *
 * Custody filter: none. Matches every other HP surface — parents in a family
 * see all kids' subscriptions equally.
 */

type Kid = { id: string; display_name: string }

type DistrictSub = {
  id: string
  platform_district_id: string
  district_name?: string
  child_id: string | null
  child_name: string | null
}

type SchoolSub = {
  id: string
  platform_school_id: string
  school_name?: string
  district_id?: string | null
  district_name?: string | null
  child_id: string | null
  child_name: string | null
}

type DistrictSearchResult = {
  id: string
  district_name: string
  state?: string
  city?: string
  school_count?: number
}

type SchoolSearchResult = {
  id: string
  school_name: string
  city?: string
  state?: string
  grade_low?: string
  grade_high?: string
  district_id?: string
  district_name?: string
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
]

interface Props {
  kids: Kid[]
}

export const DistrictAndSchoolSubscriptions: React.FC<Props> = ({ kids }) => {
  const [districtSubs, setDistrictSubs] = useState<DistrictSub[]>([])
  const [schoolSubs, setSchoolSubs] = useState<SchoolSub[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showDistrictModal, setShowDistrictModal] = useState(false)
  const [showSchoolModal, setShowSchoolModal] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [dResp, sResp] = await Promise.all([
        apiClient.get('/api/districts/subscriptions').catch(() => ({ subscriptions: [] })),
        apiClient.get('/api/schools/subscriptions').catch(() => ({ subscriptions: [] })),
      ])
      setDistrictSubs((dResp as any).subscriptions || [])
      setSchoolSubs((sResp as any).subscriptions || [])
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load subscriptions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Group schools under their district. Schools whose district isn't explicitly
  // subscribed (or doesn't report one) show under "Other schools".
  const grouped = useMemo(() => {
    const byDistrict: Record<string, { district: DistrictSub; schools: SchoolSub[] }> = {}
    districtSubs.forEach(d => {
      const key = d.platform_district_id
      if (!byDistrict[key]) byDistrict[key] = { district: d, schools: [] }
    })
    const orphans: SchoolSub[] = []
    schoolSubs.forEach(s => {
      const key = s.district_id || ''
      if (key && byDistrict[key]) byDistrict[key].schools.push(s)
      else orphans.push(s)
    })
    return { byDistrict: Object.values(byDistrict), orphans }
  }, [districtSubs, schoolSubs])

  const unsubscribeSchool = async (sub: SchoolSub) => {
    if (!confirm(`Unsubscribe from ${sub.school_name || 'this school'}${sub.child_name ? ` for ${sub.child_name}` : ''}?`)) return
    try {
      await apiClient.delete(`/api/schools/subscriptions/${sub.id}`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Unsubscribe failed')
    }
  }

  const unsubscribeDistrict = async (sub: DistrictSub) => {
    if (!confirm(`Unsubscribe from ${sub.district_name || 'this district'}? Schools in this district will stay subscribed, but you'll stop receiving district-wide calendar events.`)) return
    try {
      await apiClient.delete(`/api/districts/subscriptions/${sub.id}`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Unsubscribe failed')
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-5 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-gray-900">Subscribed Schools & Districts</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDistrictModal(true)}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Building2 className="w-4 h-4 mr-1" /> Add district
          </button>
          <button
            onClick={() => setShowSchoolModal(true)}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" /> Add school
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded">{error}</div>}
      {loading && <div className="text-gray-500 text-sm flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading subscriptions…</div>}

      {!loading && grouped.byDistrict.length === 0 && grouped.orphans.length === 0 && (
        <div className="text-gray-500 text-sm">No subscriptions yet. Add your district or the schools your kids attend to see calendar events and enable transportation features.</div>
      )}

      {grouped.byDistrict.map(({ district, schools }) => (
        <div key={district.id} className="border border-gray-200 rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-600" />
              <span className="font-semibold text-gray-900">{district.district_name || 'Unnamed district'}</span>
              {district.child_name && <span className="text-xs text-gray-500">· {district.child_name}</span>}
            </div>
            <button onClick={() => unsubscribeDistrict(district)} className="p-1 text-gray-500 hover:text-red-600" title="Unsubscribe district">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          {schools.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">No schools subscribed in this district yet.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {schools.map(s => (
                <li key={s.id} className="flex items-center justify-between px-4 py-2">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-800">{s.school_name || 'Unnamed school'}</span>
                    {s.child_name && <span className="text-xs text-gray-500">· {s.child_name}</span>}
                  </div>
                  <button onClick={() => unsubscribeSchool(s)} className="p-1 text-gray-500 hover:text-red-600" title="Unsubscribe school">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {grouped.orphans.length > 0 && (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-900 text-sm">Other schools</div>
          <ul className="divide-y divide-gray-100">
            {grouped.orphans.map(s => (
              <li key={s.id} className="flex items-center justify-between px-4 py-2">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-800">{s.school_name || 'Unnamed school'}</span>
                  {s.child_name && <span className="text-xs text-gray-500">· {s.child_name}</span>}
                </div>
                <button onClick={() => unsubscribeSchool(s)} className="p-1 text-gray-500 hover:text-red-600" title="Unsubscribe school">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showDistrictModal && (
        <DistrictSearchModal
          kids={kids}
          onClose={() => setShowDistrictModal(false)}
          onSubscribed={() => { setShowDistrictModal(false); load() }}
          onError={setError}
        />
      )}
      {showSchoolModal && (
        <SchoolSearchModal
          kids={kids}
          onClose={() => setShowSchoolModal(false)}
          onSubscribed={() => { setShowSchoolModal(false); load() }}
          onError={setError}
        />
      )}
    </div>
  )
}

// ── District search modal ────────────────────────────────────────────────

const DistrictSearchModal: React.FC<{
  kids: Kid[]
  onClose: () => void
  onSubscribed: () => void
  onError: (msg: string) => void
}> = ({ kids, onClose, onSubscribed, onError }) => {
  const [state, setState] = useState('GA')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<DistrictSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<DistrictSearchResult | null>(null)
  const [selectedKidIds, setSelectedKidIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  const doSearch = useCallback(async (q: string) => {
    setSearching(true)
    try {
      const resp: any = await apiClient.get(`/api/districts/nearby?state=${encodeURIComponent(state)}&search=${encodeURIComponent(q)}&limit=30`)
      setResults(resp.districts || [])
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || 'Search failed')
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [state, onError])

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => doSearch(query), 300)
    return () => { if (debounce.current) clearTimeout(debounce.current) }
  }, [query, doSearch])

  const toggleKid = (id: string) => {
    setSelectedKidIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const subscribe = async () => {
    if (!selected) return
    setSubmitting(true)
    try {
      const targets = selectedKidIds.length > 0
        ? selectedKidIds.map(id => ({ id, name: kids.find(k => k.id === id)?.display_name }))
        : [{ id: undefined, name: undefined }]
      await Promise.all(targets.map(t =>
        apiClient.post('/api/districts/subscribe', {
          district_id: selected.id,
          child_id: t.id,
          child_name: t.name,
        })
      ))
      onSubscribed()
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || 'Subscribe failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title="Subscribe to a district" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex gap-2">
          <select value={state} onChange={(e) => setState(e.target.value)} className="px-2 py-1 border rounded text-sm">
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex-1 relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by district name…"
              className="w-full pl-8 pr-2 py-1 border rounded text-sm"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto border rounded">
          {searching ? (
            <div className="p-3 text-sm text-gray-500 flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">No districts found. Try another state.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {results.map(r => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelected(r)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selected?.id === r.id ? 'bg-blue-50' : ''}`}
                  >
                    <div className="font-medium text-gray-900">{r.district_name}</div>
                    <div className="text-xs text-gray-500">{r.city ? `${r.city}, ` : ''}{r.state}{r.school_count ? ` · ${r.school_count} schools` : ''}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selected && kids.length > 0 && (
          <div className="border rounded p-3 space-y-2">
            <div className="text-sm text-gray-700">Assign to kids (optional — leave empty to subscribe at the family level):</div>
            <div className="flex flex-wrap gap-2">
              {kids.map(k => (
                <label key={k.id} className="inline-flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={selectedKidIds.includes(k.id)} onChange={() => toggleKid(k.id)} />
                  {k.display_name}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
          <button
            onClick={subscribe}
            disabled={!selected || submitting}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Subscribing…' : 'Subscribe'}
          </button>
        </div>
      </div>
    </ModalShell>
  )
}

// ── School search modal ─────────────────────────────────────────────────

const SchoolSearchModal: React.FC<{
  kids: Kid[]
  onClose: () => void
  onSubscribed: () => void
  onError: (msg: string) => void
}> = ({ kids, onClose, onSubscribed, onError }) => {
  const [state, setState] = useState('GA')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SchoolSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<SchoolSearchResult | null>(null)
  const [selectedKidIds, setSelectedKidIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return }
    setSearching(true)
    try {
      const resp: any = await apiClient.get(`/api/schools/search?q=${encodeURIComponent(q)}&state=${encodeURIComponent(state)}&limit=30`)
      setResults(resp.schools || [])
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || 'Search failed')
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [state, onError])

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => doSearch(query), 300)
    return () => { if (debounce.current) clearTimeout(debounce.current) }
  }, [query, doSearch])

  const toggleKid = (id: string) => {
    setSelectedKidIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const subscribe = async () => {
    if (!selected) return
    setSubmitting(true)
    try {
      const targets = selectedKidIds.length > 0
        ? selectedKidIds.map(id => ({ id, name: kids.find(k => k.id === id)?.display_name }))
        : [{ id: undefined, name: undefined }]
      await Promise.all(targets.map(t =>
        apiClient.post('/api/schools/subscribe', {
          school_id: selected.id,
          child_id: t.id,
          child_name: t.name,
        })
      ))
      onSubscribed()
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || 'Subscribe failed')
    } finally {
      setSubmitting(false)
    }
  }

  const formatGrade = (low?: string, high?: string) => {
    if (!low && !high) return ''
    if (low && high) return `Grades ${low}-${high}`
    return `Grade ${low || high}`
  }

  return (
    <ModalShell title="Subscribe to a school" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex gap-2">
          <select value={state} onChange={(e) => setState(e.target.value)} className="px-2 py-1 border rounded text-sm">
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex-1 relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by school name (min 2 chars)…"
              className="w-full pl-8 pr-2 py-1 border rounded text-sm"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto border rounded">
          {searching ? (
            <div className="p-3 text-sm text-gray-500 flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">{query.length < 2 ? 'Type at least 2 characters to search.' : 'No schools found. Try a different name or state.'}</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {results.map(r => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelected(r)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selected?.id === r.id ? 'bg-blue-50' : ''}`}
                  >
                    <div className="font-medium text-gray-900">{r.school_name}</div>
                    <div className="text-xs text-gray-500">
                      {r.city ? `${r.city}, ` : ''}{r.state}
                      {r.district_name ? ` · ${r.district_name}` : ''}
                      {formatGrade(r.grade_low, r.grade_high) ? ` · ${formatGrade(r.grade_low, r.grade_high)}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selected && kids.length > 0 && (
          <div className="border rounded p-3 space-y-2">
            <div className="text-sm text-gray-700">Assign to kids (optional — leave empty to subscribe at the family level):</div>
            <div className="flex flex-wrap gap-2">
              {kids.map(k => (
                <label key={k.id} className="inline-flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={selectedKidIds.includes(k.id)} onChange={() => toggleKid(k.id)} />
                  {k.display_name}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
          <button
            onClick={subscribe}
            disabled={!selected || submitting}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Subscribing…' : (selectedKidIds.length > 1 ? `Subscribe (${selectedKidIds.length} kids)` : 'Subscribe')}
          </button>
        </div>
      </div>
    </ModalShell>
  )
}

// ── Shared modal shell ───────────────────────────────────────────────────

const ModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-semibold text-gray-900">{title}</h4>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  )
}

export default DistrictAndSchoolSubscriptions
